<template>
  <section :class="style.sectionHeaderEditor">
    <NavContentSelect
      :text="selectedNavText"
      :actions="navActions"
      @reorder="updateNavActionsFromReorder"
    />

    <div :class="style.devices">
      <TooltipComponent position="bottom" text="Десктоп">
        <TabStyle
          :icon="desktopIcon"
          size="M"
          :active="selectedVersion === 'Десктоп'"
          :iconTone="selectedVersion === 'Десктоп' ? 'default' : 'tertiary'"
          @click="setSelectedVersion('Десктоп')"
        />
      </TooltipComponent>

      <TooltipComponent position="bottom" text="Мобильная">
        <TabStyle
          :icon="mobileIcon"
          size="M"
          :active="selectedVersion === 'Мобильная'"
          :iconTone="selectedVersion === 'Мобильная' ? 'default' : 'tertiary'"
          @click="setSelectedVersion('Мобильная')"
        />
      </TooltipComponent>
    </div>

    <div :class="style.actions">
      <TooltipComponent position="bottom" text="Открыть по ссылке">
        <GhostButton :icon="linkIcon" onlyIcon @click="previewQuiz" />
      </TooltipComponent>

      <PrimaryButton
        :icon="isCanSave ? checkDefaultIcon : checkAccentIcon"
        :disabled="!isCanSave"
        text="Сохранить"
        @click="saveData"
      />
    </div>
  </section>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import style from "./SectionHeaderEditor.module.scss";

import NavContentSelect from "@/layout/nav/components/NavContentSelect.vue";
import TabStyle from "@/elements/UI/tabs/TabStyle/TabStyle.vue";
import GhostButton from "@/elements/UI/button/ButtonStyle/Default/ghost/GhostButton.vue";
import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";

import plusIcon from "$images/icons/button/Name=Plus, Size=16, Tone=Default.svg";
import desktopIcon from "$images/icons/tabs/Name=Desktop, Size=16.svg";
import mobileIcon from "$images/icons/tabs/Name=Mobile, Size=16.svg";
import linkIcon from "$images/icons/button/Name=Link, Size=16, Tone=Default.svg";
import checkDefaultIcon from "$images/icons/button/Name=Check, Size=16, Tone=Default.svg";
import checkAccentIcon from "$images/icons/button/Name=Check, Size=16, Tone=Accent.svg";
import closeIcon from "$images/icons/button/Name=Close, Size=16, Tone=Default.svg";

import {Quiz, questions, VersionType} from "@/page/quiz/typeQuiz";
import {getBaseUrl} from "@/page/quiz/scriptQuiz";
import TooltipComponent from "@/elements/UI/overlay/Tooltip/TooltipComponent.vue";

type QuestionWithId = questions & {
  id?: string | number;
  _id?: string | number;
};

type ListReorderPayload = {
  list: ListActionItem[];
};

const CONTACTS_TYPE = "contacts";
const THANK_YOU_TYPE = "ThankYouPages";

export default defineComponent({
  name: "SectionHeaderEditor",

  components: {
    TooltipComponent,
    PrimaryButton,
    GhostButton,
    TabStyle,
    NavContentSelect
  },

  props: {
    isCanSave: {
      type: Boolean,
      default: false
    }
  },

  emits: ["save"],

  data() {
    return {
      style,

      desktopIcon,
      mobileIcon,
      linkIcon,
      checkDefaultIcon,
      checkAccentIcon,
      selectedVersion: "Десктоп" as VersionType,
      questionActionIdMap: new WeakMap<object, string>()
    };
  },

  mounted() {
    this.syncVersionFromRoute();
    this.syncSelectedQuestionFromRoute();
  },

  methods: {
    saveData(): void {
      if (!this.isCanSave) {
        return;
      }

      this.$emit("save");
    },

    getRegularQuestions(): Array<{question: questions; questionIndex: number}> {
      if (!Array.isArray(this.quiz?.questions)) {
        return [];
      }

      return this.quiz.questions
        .map((question, questionIndex) => ({
          question,
          questionIndex
        }))
        .filter(({question}) => {
          return this.isRegularQuestion(question);
        });
    },

    isRegularQuestion(question: questions): boolean {
      return (
        question.type !== CONTACTS_TYPE && question.type !== THANK_YOU_TYPE
      );
    },

    getQuestionIndexByType(type: string): number {
      if (!Array.isArray(this.quiz?.questions)) {
        return -1;
      }

      return this.quiz.questions.findIndex((question) => {
        return question.type === type;
      });
    },

    getQuestionActionId(question: questions, questionIndex: number): string {
      const questionWithId = question as QuestionWithId;

      if (questionWithId.id !== undefined) {
        return `question-id-${questionWithId.id}`;
      }

      if (questionWithId._id !== undefined) {
        return `question-id-${questionWithId._id}`;
      }

      const cachedId = this.questionActionIdMap.get(question);

      if (cachedId) {
        return cachedId;
      }

      const newId = `question-local-${Date.now()}-${questionIndex}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      this.questionActionIdMap.set(question, newId);

      return newId;
    },

    setQuizInStore(nextQuiz: Quiz): void {
      this.$quizStore.setQuiz(nextQuiz);
    },

    setQuizQuestionsInStore(nextQuestions: questions[]): void {
      const nextQuiz: Quiz = {
        ...this.quiz,
        questions: nextQuestions
      };

      this.setQuizInStore(nextQuiz);
    },

    selectQuestion(questionIndex: number): void {
      if (this.selectedQuestion === questionIndex) {
        return;
      }

      this.$quizStore.setSelectedQuestion(questionIndex);

      const currentQuery = {...this.$route.query};

      if (questionIndex === -1) {
        delete currentQuery.question;
      } else {
        currentQuery.question = String(questionIndex);
      }

      this.$router.replace({
        query: currentQuery
      });
    },

    addQuestion(): void {
      if (!Array.isArray(this.quiz?.questions)) {
        return;
      }

      const insertIndex = this.getFirstSpecialQuestionIndex();

      const newQuestion: questions = {
        variants: [],
        contacts: {},
        paragraph: "",
        type: "radio",
        question: "",
        placeholder: ""
      };

      this.questionActionIdMap.set(
        newQuestion,
        `question-local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      );

      const nextQuestions = [...this.quiz.questions];

      nextQuestions.splice(insertIndex, 0, newQuestion);

      this.setQuizQuestionsInStore(nextQuestions);
      this.selectQuestion(insertIndex);
    },

    removeQuestion(questionIndex: number): void {
      if (this.inAbTestPage) {
        return;
      }

      if (!Array.isArray(this.quiz?.questions)) {
        return;
      }

      const questionForRemove = this.quiz.questions[questionIndex];

      if (!questionForRemove || !this.isRegularQuestion(questionForRemove)) {
        return;
      }

      const selectedQuestionBeforeRemove = this.selectedQuestion;

      const nextQuestions = this.quiz.questions.filter((_, index) => {
        return index !== questionIndex;
      });

      this.questionActionIdMap.delete(questionForRemove);

      this.setQuizQuestionsInStore(nextQuestions);

      if (selectedQuestionBeforeRemove === questionIndex) {
        this.selectQuestion(-1);
        return;
      }

      if (selectedQuestionBeforeRemove > questionIndex) {
        this.selectQuestion(selectedQuestionBeforeRemove - 1);
        return;
      }

      if (selectedQuestionBeforeRemove >= nextQuestions.length) {
        this.selectQuestion(-1);
      }
    },

    getFirstSpecialQuestionIndex(): number {
      if (!Array.isArray(this.quiz?.questions)) {
        return 0;
      }

      const specialIndex = this.quiz.questions.findIndex((question) => {
        return (
          question.type === CONTACTS_TYPE || question.type === THANK_YOU_TYPE
        );
      });

      return specialIndex === -1 ? this.quiz.questions.length : specialIndex;
    },

    updateNavActionsFromReorder(payload: ListReorderPayload): void {
      this.applyQuestionsOrderFromActions(payload.list);
    },

    applyQuestionsOrderFromActions(updatedActions: ListActionItem[]): void {
      if (!Array.isArray(this.quiz?.questions)) {
        return;
      }

      const selectedQuestionBeforeReorder =
        this.quiz.questions[this.selectedQuestion];

      const regularQuestionsByActionId = new Map<string, questions>();

      this.getRegularQuestions().forEach(({question, questionIndex}) => {
        const actionId = this.getQuestionActionId(question, questionIndex);
        regularQuestionsByActionId.set(actionId, question);
      });

      const updatedRegularQuestions = updatedActions
        .filter((action) => {
          return (
            action.variant === "complex" &&
            action.id !== undefined &&
            regularQuestionsByActionId.has(String(action.id))
          );
        })
        .map((action) => {
          return regularQuestionsByActionId.get(String(action.id));
        })
        .filter((question): question is questions => {
          return Boolean(question);
        });

      const currentRegularQuestions = this.getRegularQuestions().map(
        ({question}) => question
      );

      if (updatedRegularQuestions.length !== currentRegularQuestions.length) {
        return;
      }

      const isSameRegularOrder = updatedRegularQuestions.every(
        (question, index) => {
          return question === currentRegularQuestions[index];
        }
      );

      if (isSameRegularOrder) {
        return;
      }

      const specialQuestions = this.quiz.questions.filter((question) => {
        return !this.isRegularQuestion(question);
      });

      const nextQuestions = [...updatedRegularQuestions, ...specialQuestions];

      this.setQuizQuestionsInStore(nextQuestions);

      if (selectedQuestionBeforeReorder) {
        const newSelectedIndex = nextQuestions.findIndex((question) => {
          return question === selectedQuestionBeforeReorder;
        });

        this.selectQuestion(newSelectedIndex === -1 ? -1 : newSelectedIndex);
      }
    },

    setSelectedVersion(version: VersionType): void {
      if (this.selectedVersion === version) {
        return;
      }

      this.selectedVersion = version;

      this.$quizStore.setSelectedVersionLK(version);

      this.$router.replace({
        query: {
          ...this.$route.query,
          version
        }
      });
    },

    syncVersionFromRoute(): void {
      const versionFromQuery = this.$route.query.version;

      if (versionFromQuery === "Десктоп" || versionFromQuery === "Мобильная") {
        this.selectedVersion = versionFromQuery;
      }

      this.$quizStore.setSelectedVersionLK(this.selectedVersion);
    },

    removeQuestionFromQuery(): void {
      if (!("question" in this.$route.query)) {
        return;
      }

      const currentQuery = {...this.$route.query};

      delete currentQuery.question;

      this.$router.replace({
        query: currentQuery
      });
    },

    syncSelectedQuestionFromRoute(): void {
      const questionFromQuery = this.$route.query.question;

      if (typeof questionFromQuery !== "string") {
        this.$quizStore.setSelectedQuestion(-1);
        return;
      }

      const questionIndex = Number(questionFromQuery);
      const questionsCount = Array.isArray(this.quiz?.questions)
        ? this.quiz.questions.length
        : 0;

      const isInvalidQuestionIndex =
        !Number.isInteger(questionIndex) ||
        questionIndex < 0 ||
        questionIndex >= questionsCount;

      if (isInvalidQuestionIndex) {
        this.$quizStore.setSelectedQuestion(-1);
        this.removeQuestionFromQuery();
        return;
      }

      this.$quizStore.setSelectedQuestion(questionIndex);
    },

    async previewQuiz(): Promise<void> {
      if (!this.quiz?._id) {
        return;
      }

      const domain = (await getBaseUrl()) || "/quiz/";

      if (this.quiz.customUrl) {
        window.open(domain + this.quiz.customUrl, "_blank");
        return;
      }

      window.open(domain + this.quiz._id, "_blank");
    }
  },

  computed: {
    quiz(): Quiz {
      return this.$quizStore.getQuiz;
    },

    selectedQuestion(): number {
      return this.$quizStore.getSelectedQuestion;
    },

    navActions(): ListActionItem[] {
      const actions: ListActionItem[] = [
        {
          id: "start-block",
          text: "Стартовый экран",
          icon: "transparent",
          variant: "tab",
          handler: () => this.selectQuestion(-1),
          active: this.selectedQuestion === -1
        }
      ];

      const regularQuestions = this.getRegularQuestions();

      regularQuestions.forEach(({question, questionIndex}, index) => {
        actions.push({
          id: this.getQuestionActionId(question, questionIndex),
          text: `Вопрос ${index + 1}`,
          icon: closeIcon,
          variant: "complex",
          disabled: this.inAbTestPage,
          active: questionIndex === this.selectedQuestion,
          actionHandler: () => this.removeQuestion(questionIndex),
          handler: () => this.selectQuestion(questionIndex)
        });
      });

      actions.push({
        id: "add-question",
        text: "Добавить вопрос",
        icon: plusIcon,
        variant: "button",
        handler: this.addQuestion,
        disabled: this.inAbTestPage
      });

      const contactsIndex = this.getQuestionIndexByType(CONTACTS_TYPE);
      const thanksIndex = this.getQuestionIndexByType(THANK_YOU_TYPE);

      actions.push({
        id: "contacts",
        text: "Контакты",
        icon: "transparent",
        variant: "tab",
        active: contactsIndex === this.selectedQuestion,
        handler: () => this.selectQuestion(contactsIndex)
      });

      actions.push({
        id: "thanks",
        text: "Благодарность",
        icon: "transparent",
        variant: "tab",
        disabled: this.inAbTestPage,
        active: thanksIndex === this.selectedQuestion,
        handler: () => this.selectQuestion(thanksIndex)
      });

      return actions;
    },

    selectedNavText(): string {
      if (this.selectedQuestion === -1) {
        return "Стартовый экран";
      }

      const question = this.quiz?.questions?.[this.selectedQuestion];

      if (!question) {
        return "Стартовый экран";
      }

      if (question.type === CONTACTS_TYPE) {
        return "Контакты";
      }

      if (question.type === THANK_YOU_TYPE) {
        return "Благодарность";
      }

      const regularQuestionIndex = this.getRegularQuestions().findIndex(
        ({questionIndex}) => {
          return questionIndex === this.selectedQuestion;
        }
      );

      if (regularQuestionIndex !== -1) {
        return `Вопрос ${regularQuestionIndex + 1}`;
      }

      return "Стартовый экран";
    },

    inAbTestPage(): boolean {
      return this.$route.name === "ABTest";
    },
    questionsCount(): number {
      if (!Array.isArray(this.quiz?.questions)) {
        return 0;
      }

      return this.quiz.questions.length;
    }
  },

  watch: {
    "$route.query.version"(): void {
      this.syncVersionFromRoute();
    },

    "$route.query.question"(): void {
      this.syncSelectedQuestionFromRoute();
    },

    questionsCount(): void {
      this.syncSelectedQuestionFromRoute();
    }
  }
});
</script>
